import { useState } from 'react';
import ImagePreviewModal from '@/routes/TenantLayout/SkillManagement/shared/ImagePreviewModal';
import { useAttachmentUrl } from '../api/useAttachmentUrl';

interface AttachmentImageProps {
  storagePath: string;
  alt?: string;
  maxWidth?: number;
}

export function AttachmentImage({
  storagePath,
  alt = 'attachment',
  maxWidth = 320,
}: AttachmentImageProps) {
  const { url, loading } = useAttachmentUrl(storagePath);
  const [open, setOpen] = useState(false);
  const [renderFailed, setRenderFailed] = useState(false);

  if (renderFailed && url) {
    // Chrome cannot decode some stored formats (HEIC uploads that predate
    // the worker-app conversion). Supabase signed URLs honor a `download`
    // query param that forces attachment disposition.
    const downloadUrl = `${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(alt)}`;
    return (
      <a
        href={downloadUrl}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          maxWidth,
          padding: '10px 14px',
          background: '#f0f0f0',
          borderRadius: 8,
          color: '#374151',
          fontSize: 13,
          textDecoration: 'none',
        }}
      >
        <span aria-hidden="true">📎</span>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {alt}
        </span>
        <span style={{ color: '#6b7280', flexShrink: 0 }}>
          Preview unavailable — download
        </span>
      </a>
    );
  }

  if (loading || !url) {
    return (
      <div
        style={{
          width: maxWidth,
          height: maxWidth * 0.6,
          background: '#f0f0f0',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: 12,
        }}
      >
        {loading ? 'Loading…' : 'Image unavailable'}
      </div>
    );
  }

  return (
    <>
      <img
        src={url}
        alt={alt}
        onError={() => setRenderFailed(true)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{
          maxWidth,
          maxHeight: maxWidth,
          borderRadius: 8,
          display: 'block',
          cursor: 'pointer',
        }}
      />
      <ImagePreviewModal
        isOpen={open}
        imageUrl={url}
        onClose={() => setOpen(false)}
        title={alt}
      />
    </>
  );
}
